import type { CSSProperties } from 'react';
import type { CatalogTool } from '../../api/catalogApi';
import { CH, CHD, CHV, Hero, TH, TOOL_ART, Theme4, artIndex, char, crackSVG, fx } from './art';
import Raw from './Raw';

const DEFAULT_THEME: Theme4 = ['reactor', '#8f1015', '#2b1a4a', '#f2b632'];
const VAULT_THEME: Theme4 = ['keys', '#14532d', '#052e16', '#86efac'];

interface ToolBannerProps {
  sectionId: string;
  sectionName: string;
  index: number;
  tool: CatalogTool;
  onBack: () => void;
}

function PunchEffects() {
  const shards = Array.from({ length: 10 }, (_, n) => {
    const angle = (n * 36 * Math.PI) / 180;
    return {
      size: 2 + (n % 3),
      vx: Math.round(Math.cos(angle) * 150),
      vy: Math.round(Math.sin(angle) * 150),
      delay: 0.5 + n * 0.02,
    };
  });
  return (
    <>
      <div className="ring2" />
      <div className="trail" />
      <div className="impact" />
      {shards.map((s, n) => (
        <span
          key={n}
          className="spk2"
          style={
            {
              width: s.size,
              height: s.size,
              '--sv': `translate(${s.vx}px,${s.vy}px)`,
              animationDelay: `${s.delay}s`,
            } as CSSProperties
          }
        />
      ))}
      <div className="crackwrap">
        <div className="crackflash" />
        <Raw html={crackSVG()} style={{ display: 'contents' }} />
      </div>
    </>
  );
}

export default function ToolBanner({ sectionId, sectionName, index: listIndex, tool, onBack }: ToolBannerProps) {
  const index = artIndex(sectionId, tool.id, listIndex);
  const theme: Theme4 =
    (sectionId === 'tools' && (TOOL_ART[tool.id]?.theme || TH[index])) || (sectionId === 'vault' ? VAULT_THEME : DEFAULT_THEME);
  // A tool listed in TOOL_ART owns its artwork exactly (no character when it has none); never a repeated figure.
  const own = sectionId === 'tools' ? TOOL_ART[tool.id] : undefined;
  const hero: Hero | undefined = own
    ? own.hero
    : (sectionId === 'tools' && CH[index]) || (sectionId === 'vault' ? CHV : undefined) || (sectionId === 'tools' ? undefined : CHD);
  const isThor = hero?.pr === 'hammer';

  return (
    <div
      className={`hb${isThor ? ' hbpunch' : ''}`}
      style={{ '--a': theme[1], '--b': theme[2], '--g': theme[3] } as CSSProperties}
    >
      <button className="btn hbback" onClick={onBack}>
        ← Back to {sectionName}
      </button>
      <Raw className="fx" html={fx(theme[0])} />
      {isThor && <PunchEffects />}
      {hero && <Raw className="ch" html={char(hero)} />}
      <div className="flash" />
      <div className="txt">
        <h1>{tool.name}</h1>
        <p className="lede">{tool.description}</p>
      </div>
    </div>
  );
}
